import {
  rtkApi,
  GET_ADMIN_ROLES,
  GET_ADMIN_ROLE_STAFF,
  GET_ADMIN_DEPARTMENTS,
  GET_MY_ADMIN_ACCESS,
} from '@/shared/api/rtkApi';
import type {
  AssignAdminRoleRequest,
  AssignAdminRoleResponse,
  CreateAdminRoleRequest,
  CreateAdminRoleResponse,
  CreateDepartmentRequest,
  CreateDepartmentResponse,
  DeleteAdminRoleResponse,
  DeleteDepartmentResponse,
  GetAdminRoleByIdResponse,
  GetAdminRolesParams,
  GetAdminRolesResponse,
  GetAdminRoleStaffParams,
  GetAdminRoleStaffResponse,
  GetAllAdminRolesResponse,
  GetAllDepartmentsResponse,
  GetDepartmentsParams,
  GetDepartmentsResponse,
  UpdateAdminRoleRequest,
  UpdateAdminRoleResponse,
  UpdateDepartmentRequest,
  UpdateDepartmentResponse,
  GetMyAdminAccessResponse,
} from './types';
import { normalizeEntityPermissions } from '../lib/normalizeEntityPermissions';


export const administrationApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getMyAdminAccess: build.query<GetMyAdminAccessResponse, void>({
      query: () => '/admin/roles/my-access',
      transformResponse: (response: GetMyAdminAccessResponse): GetMyAdminAccessResponse => ({
        ...response,
        data: {
          ...response.data,
          entityPermissions:
            normalizeEntityPermissions(response.data?.entityPermissions) ??
            response.data?.entityPermissions,
        },
      }),
      providesTags: [GET_MY_ADMIN_ACCESS],
    }),
    getAdminRoles: build.query<GetAdminRolesResponse, GetAdminRolesParams>({
      query: (params) => ({
        url: '/admin/roles',
        params,
      }),
      providesTags: [GET_ADMIN_ROLES],
    }),
    getAdminRoleById: build.query<GetAdminRoleByIdResponse, number>({
      query: (id) => `/admin/roles/${id}`,
      providesTags: [GET_ADMIN_ROLES],
    }),
    getAllAdminRoles: build.query<GetAllAdminRolesResponse, void>({
      query: () => '/admin/roles/all',
      providesTags: [GET_ADMIN_ROLES],
    }),
    createAdminRole: build.mutation<CreateAdminRoleResponse, CreateAdminRoleRequest>({
      query: (body) => ({
        url: '/admin/roles',
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_ADMIN_ROLES, GET_ADMIN_ROLE_STAFF],
    }),
    updateAdminRole: build.mutation<
      UpdateAdminRoleResponse,
      { id: number; body: UpdateAdminRoleRequest }
    >({
      query: ({ id, body }) => ({
        url: `/admin/roles/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [GET_ADMIN_ROLES, GET_ADMIN_ROLE_STAFF],
    }),
    deleteAdminRole: build.mutation<
      DeleteAdminRoleResponse,
      { id: number; reassignToRoleId?: number }
    >({
      query: ({ id, reassignToRoleId }) => ({
        url: `/admin/roles/${id}`,
        method: 'DELETE',
        params: reassignToRoleId ? { reassignToRoleId } : undefined,
      }),
      invalidatesTags: [GET_ADMIN_ROLES, GET_ADMIN_ROLE_STAFF],
    }),
    getAdminRoleStaff: build.query<GetAdminRoleStaffResponse, GetAdminRoleStaffParams>({
      query: (params) => ({
        url: '/admin/roles/staff',
        params,
      }),
      providesTags: [GET_ADMIN_ROLE_STAFF],
    }),
    assignAdminRole: build.mutation<AssignAdminRoleResponse, AssignAdminRoleRequest>({
      query: (body) => ({
        url: '/admin/roles/assign',
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_ADMIN_ROLE_STAFF, GET_ADMIN_ROLES],
    }),
    getDepartments: build.query<GetDepartmentsResponse, GetDepartmentsParams>({
      query: (params) => ({
        url: '/admin/departments',
        params,
      }),
      providesTags: [GET_ADMIN_DEPARTMENTS],
    }),
    getAllDepartments: build.query<GetAllDepartmentsResponse, void>({
      query: () => '/admin/departments/all',
      providesTags: [GET_ADMIN_DEPARTMENTS],
    }),
    createDepartment: build.mutation<CreateDepartmentResponse, CreateDepartmentRequest>({
      query: (body) => ({
        url: '/admin/departments',
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_ADMIN_DEPARTMENTS, GET_ADMIN_ROLES],
    }),
    updateDepartment: build.mutation<
      UpdateDepartmentResponse,
      { id: number; body: UpdateDepartmentRequest }
    >({
      query: ({ id, body }) => ({
        url: `/admin/departments/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [GET_ADMIN_DEPARTMENTS, GET_ADMIN_ROLES],
    }),
    deleteDepartment: build.mutation<DeleteDepartmentResponse, number>({
      query: (id) => ({
        url: `/admin/departments/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [GET_ADMIN_DEPARTMENTS, GET_ADMIN_ROLES],
    }),
  }),
});

export const {
  useGetMyAdminAccessQuery,
  useGetAdminRolesQuery,
  useGetAdminRoleByIdQuery,
  useGetAllAdminRolesQuery,
  useCreateAdminRoleMutation,
  useUpdateAdminRoleMutation,
  useDeleteAdminRoleMutation,
  useGetAdminRoleStaffQuery,
  useAssignAdminRoleMutation,
  useGetDepartmentsQuery,
  useGetAllDepartmentsQuery,
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  useDeleteDepartmentMutation,
} = administrationApi;
